import PhoneIcon from '@assets/icons/phone.svg?react'
import LogoIcon from '@assets/images/logo-initial.svg'
import { MMButton } from '@components'
import settings from '@configuration'
import { configuration, useLoginRequest } from 'mmfintech-checkout-commons'
import type { MerchantUserLoginRequest } from 'mmfintech-checkout-commons/types'
import { tr, useFormValues } from 'mmfintech-commons'
import { ErrorDisplay, Input } from 'mmfintech-portal-commons'
import { QRCodeSVG } from 'qrcode.react'
import { type FormEvent, useState } from 'react'

import './LoginPane.scss'

const LoginPane = ({ sessionId, session, onCancel, onSuccess }) => {
  const [step, setStep] = useState<number>(1)

  const { loginError, loginFetching, loginRequest } = useLoginRequest()

  const loginValues = useFormValues({
    email: { required: true, validation: 'email' },
    password: { required: true }
  })

  const handleLogin = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (loginValues.areValid()) {
      loginRequest(sessionId, loginValues.prepare() as MerchantUserLoginRequest, onSuccess)
    }
  }

  const getLink = () => {
    const { amount, currency } = session || {}
    return `${preparePortalLink('wallet-payment')}?sessionId=${sessionId}&amount=${amount}&currency=${currency}`
  }

  const preparePortalLink = (path: string): string => {
    const host = configuration.readBackendConfig().replace('pay', 'portal')
    return `${host}${path.substring(0, 1) === '/' ? '' : '/'}${path}`
  }

  const prepareSignupLink = (): string => {
    const link = `signup${session?.referrerMerchantId ? `?referrerMerchantId=${session.referrerMerchantId}` : ''}`
    return preparePortalLink(link)
  }

  return (
    <>
      <div className='payment-container' data-test='wallet-checkout-login'>
        {step === 1 && (
          <form className='login-pane-wrapper' noValidate onSubmit={handleLogin}>
            <div className='login-title'>{tr('CHECKOUT.LOGIN.SIGN_IN', 'Sign In')}</div>

            <div className='form-fields'>
              <Input
                type='email'
                label={tr('CHECKOUT.LOGIN.EMAIL', 'Email')}
                {...loginValues.registerInput('email')}
                disabled={loginFetching}
                data-test='email'
              />

              <Input
                type='password'
                label={tr('CHECKOUT.LOGIN.PASSWORD', 'Password')}
                {...loginValues.registerInput('password')}
                disabled={loginFetching}
                autoComplete='off'
                data-test='password'
              />

              <div className='link-wrap' data-test='forgot-password'>
                <a href={preparePortalLink('forgot-password')} target='_blank' rel='noopener noreferrer'>
                  {tr('CHECKOUT.LOGIN.FORGOT_PASSWORD', 'Forgot password?')}
                </a>
              </div>
            </div>

            <ErrorDisplay error={loginError} />

            <div className='form-actions'>
              <MMButton
                type='submit'
                variant='primary'
                caption={tr('CHECKOUT.LOGIN.BUTTON_LOGIN', 'Log in')}
                loading={loginFetching}
                data-test='button-submit'
                fullWidth
              />
              {settings.enableContinueOnMobileButton && (
                <MMButton
                  type='button'
                  variant='primary'
                  leftIcon={PhoneIcon}
                  caption={tr('CHECKOUT.LOGIN.BUTTON_MOBILE', 'Continue on mobile')}
                  onClick={() => setStep(2)}
                  disabled={loginFetching}
                  data-test='button-mobile'
                />
              )}
              <MMButton
                type='button'
                variant='secondary'
                caption={tr('CHECKOUT.BUTTONS.BACK', 'Back')}
                onClick={onCancel}
                disabled={loginFetching}
                data-test='button-back'
              />
            </div>
          </form>
        )}

        {step === 2 && (
          <form className='login-pane-wrapper'>
            <div className='login-title'>{tr('CHECKOUT.LOGIN.SCAN_CODE', 'Scan the code with Jetonbank app')}</div>

            <div className='form-fields'>
              <div className='qr-code-wrapper'>
                <div className='qr-code-container'>
                  <QRCodeSVG
                    value={getLink()}
                    size={192}
                    imageSettings={{ src: LogoIcon, height: 42, width: 42, excavate: true }}
                  />
                </div>
              </div>
            </div>

            <div className='form-actions'>
              <MMButton
                type='button'
                variant='primary'
                caption={tr('CHECKOUT.LOGIN.BUTTON_WEB', 'Continue on web')}
                onClick={() => setStep(1)}
                data-test='button-web'
                fullWidth
              />
              <MMButton
                type='button'
                variant='secondary'
                caption={tr('CHECKOUT.BUTTONS.BACK', 'Back')}
                onClick={onCancel}
                data-test='button-back'
                fullWidth
              />
            </div>
          </form>
        )}
      </div>

      <div className='signup-link-wrapper'>
        {tr('CHECKOUT.LOGIN.NO_ACCOUNT', "Don't have an account?")}
        <a href={prepareSignupLink()} data-test='sign-up' target='_blank' rel='noopener noreferrer'>
          <span>
            {tr('CHECKOUT.LOGIN.BUTTON_SIGNUP', 'Sign up')} <span className='arrow'>→</span>
          </span>
        </a>
      </div>
    </>
  )
}

export default LoginPane
